import "./webcam-popup.scss"
import { BaseComponent, BElement } from "utils/base-component"

export type WebcamPopupData = {
  name: string
  imageUrl?: string
  specialUrl?: string
  specialType?: string
  aspectRatio?: string | number
  creditLabel?: string
  creditUrl?: string
  distanceKm?: string | number
  locationName?: string
  locationUrl?: string
}

let activePopup: WebcamPopup | null = null

export class WebcamPopup extends BaseComponent {
  static cssClass = "webcam-popup"

  private initialParent!: HTMLElement | null
  private modal!: BElement | null
  private modalOverlay!: BElement | null
  private modalClose!: BElement | null
  private modalImage!: BElement | null
  private modalSpecial!: BElement | null
  private modalTitle!: BElement | null
  private modalCredit!: BElement | null
  private modalCreditLink!: BElement | null
  private modalDistance!: BElement | null
  private modalLocation!: BElement | null
  private modalLocationLink!: BElement | null

  private get creditLink(): HTMLAnchorElement | null {
    return this.modalCreditLink?.element as HTMLAnchorElement | null
  }

  private get locationLink(): HTMLAnchorElement | null {
    return this.modalLocationLink?.element as HTMLAnchorElement | null
  }

  private get modalImageElement(): HTMLImageElement | null {
    return this.modalImage?.element as HTMLImageElement | null
  }

  init() {
    activePopup = this
    this.initialParent = this.root.parentElement
    this.modal = this.element("modal")
    this.modalOverlay = this.element("modal-overlay")
    this.modalClose = this.element("modal-close")
    this.modalImage = this.element("modal-image")
    this.modalSpecial = this.element("modal-special")
    this.modalTitle = this.element("modal-title")
    this.modalCredit = this.element("modal-credit")
    this.modalCreditLink = this.element("modal-credit-link")
    this.modalDistance = this.element("modal-distance")
    this.modalLocation = this.element("modal-location")
    this.modalLocationLink = this.element("modal-location-link")

    this.modalOverlay?.element.addEventListener("click", (event) => {
      event.preventDefault()
      this.close()
    })
    this.modalClose?.element.addEventListener("click", (event) => {
      event.preventDefault()
      this.close()
    })
  }

  open(data: WebcamPopupData) {
    if (!this.modal || !this.modalTitle) return

    this.mountInVisibleRoot()
    this.renderMedia(data)
    this.modalTitle.element.textContent = data.name

    if (this.modalCredit && this.modalCreditLink) {
      const hasCreditLink = Boolean(data.creditLabel && data.creditUrl)
      this.modalCredit.toggleModifier("hidden", !hasCreditLink)
      if (hasCreditLink && this.creditLink) {
        this.modalCreditLink.element.textContent = data.creditLabel || ""
        this.creditLink.href = data.creditUrl || "#"
      }
    }

    if (this.modalDistance) {
      const hasDistance = data.distanceKm !== undefined && data.distanceKm !== null
      const distanceValue = hasDistance ? Number(data.distanceKm) : null
      const distanceText = distanceValue && distanceValue > 0 ? `Distance: ${distanceValue} km` : ""
      this.modalDistance.toggleModifier("hidden", !distanceText)
      this.modalDistance.element.textContent = distanceText
    }

    if (this.modalLocation && this.modalLocationLink) {
      const hasLocationLink = Boolean(data.locationName && data.locationUrl)
      this.modalLocation.toggleModifier("hidden", !hasLocationLink)
      if (hasLocationLink && this.locationLink) {
        this.modalLocationLink.element.textContent = data.locationName || ""
        this.locationLink.href = data.locationUrl || "#"
      }
    }

    document.addEventListener("keydown", this.handleEscape)
    this.modal.toggleModifier("hidden", false)
  }

  close() {
    if (this.modalSpecial) {
      this.modalSpecial.element.innerHTML = ""
      this.modalSpecial.element.removeAttribute("style")
    }

    if (this.modalImage && this.modalImageElement) {
      this.modalImage.toggleModifier("hidden", false)
      this.modalImageElement.src = ""
      this.modalImageElement.alt = ""
    }

    this.modalSpecial?.toggleModifier("hidden", true)
    this.modal?.toggleModifier("hidden", true)
    document.removeEventListener("keydown", this.handleEscape)
    this.restoreInitialParent()
  }

  private renderMedia(data: WebcamPopupData) {
    if (!this.modalImage || !this.modalSpecial) return

    if (data.specialUrl && data.specialType !== "image") {
      const iframe = document.createElement("iframe")
      iframe.setAttribute("frameborder", "0")
      iframe.setAttribute("webkitallowfullscreen", "true")
      iframe.setAttribute("mozallowfullscreen", "true")
      iframe.setAttribute("allowfullscreen", "true")
      iframe.setAttribute("allow", "fullscreen")
      iframe.setAttribute("src", data.specialUrl)

      this.modalSpecial.element.innerHTML = ""
      this.modalSpecial.element.appendChild(iframe)
      this.modalSpecial.toggleModifier("hidden", false)

      if (data.aspectRatio) {
        this.modalSpecial.element.style.paddingTop = `${Number(data.aspectRatio) * 100}%`
      } else {
        this.modalSpecial.element.removeAttribute("style")
      }

      if (this.modalImageElement) {
        this.modalImage.toggleModifier("hidden", true)
        this.modalImageElement.src = ""
        this.modalImageElement.alt = ""
      }
      return
    }

    this.modalSpecial.element.innerHTML = ""
    this.modalSpecial.element.removeAttribute("style")
    this.modalSpecial.toggleModifier("hidden", true)
    if (this.modalImage && this.modalImageElement) {
      this.modalImage.toggleModifier("hidden", false)
      this.modalImageElement.src =
        data.specialType === "image" && data.specialUrl ? data.specialUrl : data.imageUrl || ""
      this.modalImageElement.alt = data.name
    }
  }

  private handleEscape = (event: KeyboardEvent) => {
    if (event.key === "Escape") this.close()
  }

  private mountInVisibleRoot() {
    const fullscreenRoot = document.fullscreenElement as HTMLElement | null
    if (fullscreenRoot && this.root.parentElement !== fullscreenRoot) {
      fullscreenRoot.appendChild(this.root)
      return
    }

    this.restoreInitialParent()
  }

  private restoreInitialParent() {
    if (this.initialParent && this.root.parentElement !== this.initialParent) {
      this.initialParent.appendChild(this.root)
    }
  }
}

export function openWebcamPopup(data: WebcamPopupData) {
  activePopup?.open(data)
}
